import { useEffect, useRef } from "react"

import { KARAOKE_LINES } from "@/constants/karaoke"
import { postJson } from "@/utils/api"

/**
 * Teleprompter controls for Karaoke mode. Renders nothing. Two bare keys,
 * captured globally (no modifier combos — those get intercepted by the OS), and
 * armed only while `enabled` (i.e. still in karaoke mode, pre-fork):
 *
 *   1  advance — type the NEXT scripted trainee line into the chat input (with a
 *      human-feel typewriter effect, handled by SimulationMessageAction) and
 *      submit it through the normal send path. Dispatched as a `karaoke-type`
 *      window event so the input component owns the typing/submit.
 *   0  fork to live — hand the conversation to the real LLM.
 *
 * The keys are swallowed (preventDefault) while armed so they never leak into
 * the textarea; after a fork the parent flips `enabled` off and digits type
 * normally again. The line index is tracked locally (lag-free) and reset when
 * the conversation changes.
 */
export default function KaraokePresenter({
  conversationId,
  scriptId,
  enabled,
  ready,
  onForked,
}: {
  conversationId?: number
  scriptId?: string | null
  enabled: boolean
  ready: boolean
  onForked?: () => void
}) {
  const nextIndexRef = useRef(0)
  useEffect(() => {
    nextIndexRef.current = 0
  }, [conversationId])

  // Mirror latest props into a ref so the capture listener stays stable.
  const stateRef = useRef({
    conversationId,
    scriptId,
    enabled,
    ready,
    onForked,
  })
  stateRef.current = { conversationId, scriptId, enabled, ready, onForked }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = stateRef.current
      if (!s.enabled || !s.conversationId) return
      if (e.altKey || e.ctrlKey || e.metaKey) return
      if (e.key !== "0" && e.key !== "1") return

      // Armed: these keys drive the teleprompter, never the textarea.
      e.preventDefault()
      e.stopPropagation()

      if (e.key === "1") {
        if (!s.ready) return
        const lines = (s.scriptId ? KARAOKE_LINES[s.scriptId] : []) ?? []
        const text = lines[nextIndexRef.current]
        if (!text) return
        nextIndexRef.current += 1
        window.dispatchEvent(
          new CustomEvent("karaoke-type", { detail: { text } }),
        )
      } else {
        void (async () => {
          try {
            await postJson(
              `/api/conversations/${s.conversationId}/karaoke/fork_to_live`,
              {},
            )
            s.onForked?.()
          } catch (err) {
            console.error("Karaoke fork failed", err)
          }
        })()
      }
    }

    window.addEventListener("keydown", onKey, true)
    return () => window.removeEventListener("keydown", onKey, true)
  }, [])

  return null
}
